import { Dispatch, SetStateAction, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import ApplicationCreatedModal from "./ApplicationCreatedModal";
import ApplicationPendingModal from "./ApplicationPendingModal";
import SelfManagedGuarantorsModal from "../../components/SelfManagedGuarantorsModal/SelfManagedGuarantorsModal";

type PendingStage = "pending" | "selfManaged" | null;

type Props = {
  createdApp: {
    title: string;
    peopleCount: number;
    pendingGuarantors?: { name: string; email?: string }[];
  } | null;
  user: any;
  pendingStage: PendingStage;
  setPendingStage: Dispatch<SetStateAction<PendingStage>>;
  onClose: () => void;
};

function getPending(createdApp: Props["createdApp"], user: any) {
  const raw =
    createdApp?.pendingGuarantors ??
    (user?.tenant_extra_field_id?.selected_onboarding_users ?? [])
      .filter((u: any) => u?.type === "guarantor")
      .map((u: any) => ({ name: u.name, email: u.email }));
  const added = user?.gurantor_added_id ?? [];
  return raw.map((p: any) => ({
    ...p,
    completed: added.some((a: any) => {
      const pendingEmail = p?.email?.toLowerCase();
      if (pendingEmail) {
        return (
          a?.gurantor_email?.toLowerCase() === pendingEmail ||
          a?.guarantor_id?.email?.toLowerCase() === pendingEmail
        );
      }
      const pendingName = p?.name?.trim().toLowerCase();
      const addedName =
        `${a?.guarantor_id?.fname ?? ""} ${a?.guarantor_id?.lname ?? ""}`
          .trim()
          .toLowerCase();
      return !!pendingName && pendingName === addedName;
    }),
  }));
}

export default function PostApplicationModals({
  createdApp,
  user,
  pendingStage,
  setPendingStage,
  onClose,
}: Props) {
  const navigate = useNavigate();
  useEffect(() => {
    if (!createdApp) return;
    const pending = getPending(createdApp, user);
    if (pending.length > 0 && !pendingStage) setPendingStage("pending");
  }, [createdApp, user, pendingStage, setPendingStage]);

  if (!createdApp) return null;
  const pending = getPending(createdApp, user);
  const pendingCount = pending.filter(
    (guarantor: any) => !guarantor.completed
  ).length;
  const hasPending = pendingCount > 0;

  if (hasPending && pendingStage === "selfManaged") {
    return (
      <div
        className="modal-fixed-screen"
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 80,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "var(--skor-color-canvas, #f6f6f6)",
          padding: "24px 16px",
        }}
      >
        <SelfManagedGuarantorsModal
          guarantors={pending}
          onLater={() => {
            setPendingStage(null);
            onClose();
            navigate("/home?welcome=true");
          }}
          inline
        />
      </div>
    );
  }
  if (hasPending) {
    return (
      <ApplicationPendingModal
        title={createdApp.title}
        peopleCount={createdApp.peopleCount}
        pendingCount={pendingCount}
        onProceed={() => setPendingStage("selfManaged")}
        onLater={() => {
          setPendingStage(null);
          onClose();
          navigate("/home?welcome=true");
        }}
      />
    );
  }
  return (
    <ApplicationCreatedModal
      title={createdApp.title}
      peopleCount={createdApp.peopleCount}
      onClose={() => {
        setPendingStage(null);
        onClose();
      }}
    />
  );
}
